/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {
    Debug,
    EDataTypeType,
    MemoryManagement,
    PointCloud,
    RenderDocument,
    UPDATETYPES,
    WebGLContext
} from "js_renderer";
import AbstractModule from "../abstract_module";
import {vec2} from "gl-matrix";
import {EOutlinesStyle} from "./scene_render_settings_enums";
import IRenderSettingsModule from "./scene_render_settings_interface";
import Scene from "../../scene";
import Settings from "../settings";
import BoundingBoxElement from "../../elements/bounding_box";
import IBoundingBox from "../../bounding_box_interface";

interface ITexturing {
    disableTextures?: boolean;
    resolutionLimit?: number;
    frustumCulling?: boolean;
    enableCompression: boolean;
    storeTextureData?: boolean;
}

interface ITransparency {
    elementsTransparencyEnabled?: boolean;
    globalTransparencyOverrideEnabled?: boolean;
    globalTransparencyOverrideValue?: number;
}

interface IRendering {
    forceBackfaceCullingOff?: boolean;
    boundingBox?: boolean;
    wireframe?: boolean;
    showOutlines?: boolean;
    outlinesStyle?: EOutlinesStyle;
    xray?: boolean;
    forceLogDepthBufferDisabled?: boolean;
    logDepthBufferEnabled?: boolean;
    showTransparentObjects?: boolean;
    transparency?: ITransparency;
    keepCameraAboveSceneBBoxBottomPlane?: boolean;
    forceKeepCameraAboveAllLayers?: boolean;
}

interface IRenderOptionSettings {
    geometry?: IRendering;
    textures?: ITexturing;
}

export default
class RenderSettingsModule extends AbstractModule implements IRenderSettingsModule {

    protected static _typeName: string = "renderSettings";
    private static TEXTURE_RESOLUTION_UNLIMITTED: number = -1;
    private static TEXTURE_RESOLUTION_MAX: number = 16384;

    protected _defaultSettings: IRenderOptionSettings;
    protected _settings: IRenderOptionSettings;

    private _qualityPerformanceFactor: number;
    private _modelStatsEventEnabled: boolean;

    private useETC1: boolean = false;
    private useDDS: boolean = false;
    private usePVRTC: boolean = false;

    private textureLODDebuggingEnabled: boolean = false;
    private geometryLODDebuggingEnabled: boolean = false;

    private _boundingBoxSAPIElement: BoundingBoxElement;

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        this._type = RenderSettingsModule._typeName;

        this._defaultSettings = {
            geometry: {
                forceBackfaceCullingOff: false,
                boundingBox: false,
                wireframe: false,
                showOutlines: true,
                outlinesStyle: EOutlinesStyle.COLOR,
                xray: false,
                forceLogDepthBufferDisabled: false,
                logDepthBufferEnabled: false,
                showTransparentObjects: true,
                transparency: {
                    elementsTransparencyEnabled: true,
                    globalTransparencyOverrideEnabled: false,
                    globalTransparencyOverrideValue: 0.0
                },
                keepCameraAboveSceneBBoxBottomPlane: false,
                forceKeepCameraAboveAllLayers: false
            },
            textures: {
                resolutionLimit: RenderSettingsModule.TEXTURE_RESOLUTION_UNLIMITTED,
                disableTextures: false,
                frustumCulling: false,
                enableCompression: false,
                storeTextureData: false
            }
        };

        RenderSettingsModule.TEXTURE_RESOLUTION_MAX = WebGLContext.caps.MAX_TEXTURE_SIZE;

        this._settings = Settings.clone(this._defaultSettings);

        this._qualityPerformanceFactor = window.devicePixelRatio === undefined ? 1 : window.devicePixelRatio;

        this._modelStatsEventEnabled = false;

        this._boundingBoxSAPIElement = null;

        this.renderDocument.registerObserver(this);
    }

    public static getClassType(): string {
        return RenderSettingsModule._typeName;
    }

    public setGPUTexturesMemLimit(aLimitInMB: number) {
        MemoryManagement.setGPUTexturesMemLimit(aLimitInMB);
        this.renderDocument.getRenderer().setGPUTexturesMemLimit(aLimitInMB);
    }

    public setTextureLODDebugEnabled(aValue: boolean) {
        this.textureLODDebuggingEnabled = aValue;
    }

    public setGeometryLODDebugEnabled(aValue: boolean) {
        this.geometryLODDebuggingEnabled = aValue;
    }

    public setModelStatisticsEventDispatchingEnabled(aValue: boolean) {
        this._modelStatsEventEnabled = aValue;
    }

    public getShowBoundingBox(): boolean {
        return this._settings.geometry.boundingBox;
    }

    public getOutlinesStyle(): EOutlinesStyle {
        return this._settings.geometry.outlinesStyle;
    }

    public getShowOutlines(): boolean {
        return this._settings.geometry.showOutlines;
    }

    public getShowTransparentObjects(): boolean {
        return this._settings.geometry.showTransparentObjects;
    }

    public getElementTransparencyEnabled(): boolean {
        return this._settings.geometry.transparency.elementsTransparencyEnabled;
    }

    public getForceGlobalTransparencyOverride(): boolean {
        return false; // do not use the global transparency override until we solve the rendering of all objects transparent but with low transparncy (the wavg method is not suitable for that)
        return this._settings.geometry.transparency.globalTransparencyOverrideEnabled;
    }

    public getForceGlobalTransparencyOverrideValue(): number {
        return this._settings.geometry.transparency.globalTransparencyOverrideValue;
    }

    public getRenderXRay(): boolean {
        return this._settings.geometry.xray;
    }

    public getQualityPerformanceFactor(): number {
        return this._qualityPerformanceFactor;
    }

    /**
     * Get the size of all points for all pointcloudlod elements.
     *
     * @returns {*|number}
     */
    public getPointCloudPointSize(/*aModelId*/): number {
        return PointCloud.pointSize;
    }

    public getForceBackFaceCullingOff(): boolean {
        return this._settings.geometry.forceBackfaceCullingOff;
    }

    public getViewFrustumCullingForTexturesSwitching(): boolean {
        return this._settings.textures.frustumCulling;
    }

    public getTexturesResolutionLimit(): number {
        return this._settings.textures.resolutionLimit;
    }

    public getGPUTexturesMemLimit(): number {
        return MemoryManagement.getGPUTexturesMemLimit();
    }

    public getStoreDownloadedTextureDataInMemory(): boolean {
        return this._settings.textures.storeTextureData;
    }

    public getTextureLODDebugEnabled(): boolean {
        return this.textureLODDebuggingEnabled;
    }

    public getGeometryLODDebugEnabled(): boolean {
        return this.geometryLODDebuggingEnabled;
    }

    public getModelStatisticsEventDispatchingEnabled(): boolean {
        return this._modelStatsEventEnabled;
    }

    public getTexturesCompressionEnabled(): { enabled: boolean, type: EDataTypeType } {
        if (this.usePVRTC) {
            return {
                enabled: this._settings.textures.enableCompression,
                type: EDataTypeType.pvrImageTexture
            };
        }
        else if (this.useDDS) {
            return {
                enabled: this._settings.textures.enableCompression,
                type: EDataTypeType.ddsImageTexture
            };
        }
        else if (this.useETC1) {
            return {
                enabled: this._settings.textures.enableCompression,
                type: EDataTypeType.etc1ImageTexture
            };
        }

        return {
            enabled: false,
            type: EDataTypeType.imageTexture
        };
    }

    public getLogarithmicDepthBufferSupportEnabled(): boolean {
        return this._settings.geometry.logDepthBufferEnabled;
    }

    public getKeepCameraAboveSceneBBoxBottomPlane(): boolean {
        return this._settings.geometry.keepCameraAboveSceneBBoxBottomPlane;
    }

    public getTexturesRenderingDisabled(): boolean {
        return this._settings.textures.disableTextures;
    }

    public getWireframeRendering(): boolean {
        return this._settings.geometry.wireframe;
    }

    public getForceKeepCameraAboveAllLayers(): boolean {
        return this._settings.geometry.forceKeepCameraAboveAllLayers;
    }

    public viewChanged() {
        //
    }

    public viewResized(aContentSize: vec2) {
        //
    }

    public setFromModelDescription(aJsonObject: any) {
        let settings: IRenderOptionSettings = {};

        if (aJsonObject.hasOwnProperty("ModelConfiguration")) {
            let modelConfig: any = aJsonObject.ModelConfiguration;

            if (modelConfig.hasOwnProperty("keepCameraAboveSceneBBoxBottomPlane")) {
                if (!settings.hasOwnProperty("geometry")) {
                    settings.geometry = {};
                }

                settings.geometry.keepCameraAboveSceneBBoxBottomPlane = modelConfig.keepCameraAboveSceneBBoxBottomPlane;
            }

            if (modelConfig.hasOwnProperty("forceKeepCameraAboveAllLayers")) {
                if (!settings.hasOwnProperty("geometry")) {
                    settings.geometry = {};
                }

                settings.geometry.forceKeepCameraAboveAllLayers = modelConfig.forceKeepCameraAboveAllLayers;
            }

            if (modelConfig.hasOwnProperty("elementsTransparencyEnabled")) {
                if (!settings.hasOwnProperty("geometry")) {
                    settings.geometry = {};
                }

                if (!settings.geometry.hasOwnProperty("transparency")) {
                    settings.geometry.transparency = {};
                }

                settings.geometry.transparency.elementsTransparencyEnabled = modelConfig.elementsTransparencyEnabled;
            }

            if (modelConfig.hasOwnProperty("globalTransparencyOverrideEnabled")) {
                if (!settings.hasOwnProperty("geometry")) {
                    settings.geometry = {};
                }

                if (!settings.geometry.hasOwnProperty("transparency")) {
                    settings.geometry.transparency = {};
                }

                settings.geometry.transparency.globalTransparencyOverrideEnabled = modelConfig.globalTransparencyOverrideEnabled;
            }

            if (modelConfig.hasOwnProperty("globalTransparencyOverrideValue")) {
                if (!settings.hasOwnProperty("geometry")) {
                    settings.geometry = {};
                }

                if (!settings.geometry.hasOwnProperty("transparency")) {
                    settings.geometry.transparency = {};
                }

                settings.geometry.transparency.globalTransparencyOverrideValue = modelConfig.globalTransparencyOverrideValue;
            }
        }

        if (aJsonObject.hasOwnProperty("ModelDrawSettings")) {
            let modelDrawSettings: any = aJsonObject.ModelDrawSettings;
            if (modelDrawSettings.hasOwnProperty("DrawStyle")) {
                if (!settings.hasOwnProperty("geometry")) {
                    settings.geometry = {};
                }

                switch (modelDrawSettings.DrawStyle.style) {
                    case "USER_DEFINED":
                    case "COLOR_OUTLINES":
                        settings.geometry.outlinesStyle = EOutlinesStyle.COLOR;
                        break;
                    case "BLACK_OUTLINES":
                        settings.geometry.outlinesStyle = EOutlinesStyle.BLACK_AND_WHITE;
                        break;
                    case "NO_OUTLINES":
                        settings.geometry.outlinesStyle = EOutlinesStyle.NO_OUTLINES;
                        break;
                    default:
                        settings.geometry.outlinesStyle = EOutlinesStyle.COLOR;
                        break;
                }

            }
        }

        if (aJsonObject.hasOwnProperty(this.getType())) {
            settings = aJsonObject[this.getType()];
        }

        this.set(settings);
    }

    /**
     * Set the size of all points for all pointcloudlod elements together (because the updates of the render graph is not yet ready)
     *
     * @param {float} aPointSize size of the rendered points in pixels
     */
    public setPointCloudPointSize(/*aModelId, */aPointSize: number) {
        PointCloud.pointSize = aPointSize;

        // request render of the scene
        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    public setPauseTexturesSwitching(aValue: boolean) {
        this.renderDocument.getRenderer().pauseTexturesSwitching(aValue);
    }

    public onBoundingBoxUpdated(aBBox: IBoundingBox, aBBoxForNPC: IBoundingBox) {
        if (this._settings.geometry.boundingBox) {
            let _sceneAPIBBox = this.sceneDocument.getSBoundingBoxForNPC();
            this._boundingBoxSAPIElement.setPositions(_sceneAPIBBox.getCenter(), _sceneAPIBBox.getDimensions());
        }
    }

    protected showBoundingBox(aValue: boolean) {

        if (this._settings.geometry.boundingBox) {
            this._boundingBoxSAPIElement = this.sceneDocument.createAPIElement(BoundingBoxElement.TypeName) as BoundingBoxElement;
            this.sceneDocument.attachElementToInternalModel(this._boundingBoxSAPIElement);

            let _sceneAPIBBox = this.sceneDocument.getSBoundingBoxForNPC();

            this._boundingBoxSAPIElement.setPositions(_sceneAPIBBox.getCenter(), _sceneAPIBBox.getDimensions());
            this._boundingBoxSAPIElement.extendProperties({clippable: false});
            this._boundingBoxSAPIElement.draw(true);
        }
        else if (!this._settings.geometry.boundingBox) {
            if (this._boundingBoxSAPIElement) {
                this._boundingBoxSAPIElement.remove();
                this._boundingBoxSAPIElement = null;
            }
        }

        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    protected showAllBoundingBoxes(aValue: boolean) {
        /*if (aValue && !this._boundingBoxStyle.enabled) {

            let models = this.renderDocument.getModelsList();

            for (let k = 0; k < models.length; k++) {
                let geometryObjects = models[k].getGeometryObjects();
                for (let i = 0; i < geometryObjects.length; i++) {
                    let SAPIElement: BoundingBoxElement = <BoundingBoxElement>this.sceneDocument.createAPIElement(BoundingBoxElement.TypeName);
                    this.sceneDocument.attachElementToInternalModel(SAPIElement);

                    let element = geometryObjects[i].getElement().getSceneAPIElementReference();
                    let bbox = new RenderBoundingBox(element._properties.bboxMin, element._properties.bboxMax);

                    if (element._metadata.level > 3) {
                        console.log("tile: level " + element._metadata.level + ": " + element._metadata.row + " " + element._metadata.col);
                    }

                    //if (element._metadata.row == 1 && element._metadata.col == 1) {
                    SAPIElement.setPositions(bbox.getCenter(), bbox.getDimensions());
                    SAPIElement.extendProperties({clippable: false});
                    SAPIElement.draw(true);

                    this._boundingBoxStyle.SAPIElements.push(SAPIElement);
                    //}
                }
            }
        } else if (!aValue && this._boundingBoxStyle.enabled) {
            for (let i = 0; i < this._boundingBoxStyle.SAPIElements.length; i++) {
                this._boundingBoxStyle.SAPIElements[i].remove();
            }

            this._boundingBoxStyle.SAPIElements = [];
        }

        this._boundingBoxStyle.enabled = aValue;

        this.sceneAPI.viewModeModule.scheduleRedraw();*/
    }

    /**
     * Force outlines to be visible or hidden, independent on group tree visibility.
     *
     * @param {boolean} aValue
     */
    protected setShowOutlines(aValue: boolean) {

        // get all outlines from all models excluding external model and set their visibility to false or
        // to visibility of its assigned model element (affected by what is set in group tree).
        let models = this.renderDocument.getModelsList();
        for (let key in models) {
            let model = models[key];
            let geometryObjects = model.getGeometryObjects();
            for (let shape of geometryObjects) {
                // as from 3D and iv file we does not have an information whether line is an an outline, then we handle
                // all lines with single pixel width as outlines here.
                // if (shape.isOutline()) { // do not consider lines with 1pixel width as outlines as it is wron
                                            // g behavior (unfortunatelly iv models has outlines defines as lines with
                                            // 1px width, so these ivs will be not correctly handled here)
                if (shape.getClassTypeName() === "OUTLINE") { // TODO: export shape typenames (OUTLINE) from renderer (type_names.ts or st. like it)
                    shape._forceHidden = !this._settings.geometry.wireframe && (!this._settings.geometry.showOutlines || this._settings.geometry.outlinesStyle == EOutlinesStyle.NO_OUTLINES);
                }

                shape.setNeedUpdate([UPDATETYPES.VISIBILITY]);
            }
        }

        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    /**
     * Force show transparent objects or now
     *
     * @param {boolean} aValue
     */
    protected setShowTransparentObjects(aValue: boolean) {
        // get all outlines from all models excluding external model and set their visibility to false or
        // to visibility of its assigned model element (affected by what is set in group tree).
        let models = this.renderDocument.getModelsList();
        for (let key in models) {
            let model = models[key];
            let geometryObjects = model.getGeometryObjects();
            for (let shape of geometryObjects) {
                // as from 3D and iv file we does not have an information whether line is an outline, then we handle
                // all lines with single pixel width as outlines here.
                if (!shape.isOpaque()) {
                    shape._forceHidden = !aValue;
                }

                shape.setNeedUpdate([UPDATETYPES.VISIBILITY]);
            }
        }

        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    /**
     * Enable XRay rendering of models (except the local model)
     *
     * @param {boolean} aValue
     */
    protected setRenderXRay(aValue: boolean) {
        if (aValue) {
            this.sceneAPI.getBackgroundModule().forceBlackBackground(true);
        } else {
            this.sceneAPI.getBackgroundModule().forceBlackBackground(false);
            // this.sceneAPI.getBackgroundModule().applyBackgroundColor();
        }

        // recompile shaders
        let webglRenderGraph = this.renderDocument.getRenderer().getWebGLRenderGraph();
        if (webglRenderGraph) {
            webglRenderGraph.initializeShaders();
        }

        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    protected setViewFrustumCullingForTexturesSwitching(aEnabled: boolean) {
        this.renderDocument.getRenderer().setViewFrustumCullingForTexturesSwitching(aEnabled);
    }

    /**
     * This function sets the maximum resolution of both downloaded and used texture resolution in the model.
     *
     * @param  {number} aValue Maximum resolution of the longer side of the texture
     */
    protected limitTexturesResolution(aValue: number) {

        let newLimit = aValue;

        if (aValue === RenderSettingsModule.TEXTURE_RESOLUTION_UNLIMITTED) {
            newLimit = RenderSettingsModule.TEXTURE_RESOLUTION_MAX;
        }

        if (newLimit > RenderSettingsModule.TEXTURE_RESOLUTION_MAX) {
            newLimit = RenderSettingsModule.TEXTURE_RESOLUTION_MAX;
            Debug.logInfo(
                "Warning: Cannot set texture resolution limit to " + aValue + " as it exceed the device supported resolution of " +
                RenderSettingsModule.TEXTURE_RESOLUTION_MAX + "."
            );
        } else if (newLimit < 0) {
            newLimit = 0;
            Debug.logInfo(
                "Warning: Cannot set texture resolution limit to " + aValue + " as it is lower than zero."
            );
        }

        if (this._settings.textures.resolutionLimit !== newLimit) { // do the change
            this._settings.textures.resolutionLimit = newLimit;
            this.renderDocument.limitTexturesResolution(newLimit);
        }
    }

    protected setEnableWireframeRendering(aValue: boolean) {

        // get all outlines from all models excluding external model and set their visibility to false or
        // to visibility of its assigned model element (affected by what is set in group tree).
        let models = this.renderDocument.getModelsList();
        for (let key in models) {
            let model = models[key];
            let geometryObjects = model.getGeometryObjects();
            for (let shape of geometryObjects) {
                if (shape.getClassTypeName() === "OUTLINE") { // TODO: export shape typenames from renderer
                    shape._forceHidden = !this._settings.geometry.showOutlines && !this._settings.geometry.wireframe;
                }
                else if (shape.getClassTypeName() === "LINE") { // TODO: export shape typenames from renderer
                    shape._forceHidden = false; // we would like to keep lines visible in wireframe mode always
                }
                else {
                    shape._forceHidden = this._settings.geometry.wireframe;
                }

                shape.setNeedUpdate([UPDATETYPES.VISIBILITY]);
            }
        }

        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    protected setEnableLogarithmicDepthBufferSupport(aValue: boolean) {
        if (this._settings.geometry.forceLogDepthBufferDisabled) {
            Debug.logInfo("Logarithmic depth buffer is disabled (forced).");
            aValue = false;
        }

        // check if gl_FragDepthEXT extension is available
        if (!WebGLContext.hasFragDepth) {
            if (aValue) {
                Debug.logInfo("Cannot use logarithmic depth buffer because the gl_FragDepthEXT is not supported on this device.");

                // override the required settings
                this.set({
                    geometry: {
                        logDepthBufferEnabled: false
                    }
                });

                return;
            }
        }

        this.sceneAPI.viewModeModule.initLogarithmicDepthBuffer();
    }

    protected setEnableTexturesCompression() {

        this.usePVRTC = false;
        this.useDDS = false;
        this.useETC1 = false;

        // check the extensions
        if (WebGLContext.getFeatureAvailable("COMPRESSED_TEXTURE_PVRTC")) {
            this.usePVRTC = true;
        } else if (WebGLContext.getFeatureAvailable("COMPRESSED_TEXTURE_S3TC")) {
            this.useDDS = true;
        } else if (WebGLContext.getFeatureAvailable("COMPRESSED_TEXTURE_ETC1")) {
            this.useETC1 = true;
        }
    }

    protected applySettings(aChangedSettings: any, aPreviousSettings: any) {

        if (aChangedSettings.hasOwnProperty("geometry")) {

            if (aChangedSettings.geometry.hasOwnProperty("boundingBox")) {
                this.showBoundingBox(aChangedSettings.geometry.boundingBox);
            }

            if (aChangedSettings.geometry.hasOwnProperty("wireframe")) {
                this.setEnableWireframeRendering(aChangedSettings.geometry.wireframe);
            }

            if (aChangedSettings.geometry.hasOwnProperty("xray")) {
                this.setRenderXRay(aChangedSettings.geometry.xray);
            }

            if (aChangedSettings.geometry.hasOwnProperty("showTransparentObjects")) {
                this.setShowTransparentObjects(aChangedSettings.geometry.showTransparentObjects);
            }

            if (aChangedSettings.geometry.hasOwnProperty("showOutlines")) {
                this.setShowOutlines(aChangedSettings.geometry.showOutlines);
            }

            if (aChangedSettings.geometry.hasOwnProperty("logDepthBufferEnabled")) {
                this.setEnableLogarithmicDepthBufferSupport(aChangedSettings.geometry.logDepthBufferEnabled);
            }

            if (aChangedSettings.geometry.hasOwnProperty("forceLogDepthBufferDisabled")) {
                if (aChangedSettings.geometry.forceLogDepthBufferDisabled) {
                    this.setEnableLogarithmicDepthBufferSupport(false);
                }
            }

            if (aChangedSettings.geometry.hasOwnProperty("forceBackfaceCullingOff")) {
                this.sceneAPI.viewModeModule.scheduleRedraw();
            }
        }

        if (aChangedSettings.hasOwnProperty("textures")) {

            if (aChangedSettings.textures.hasOwnProperty("resolutionLimit")) {
                this.limitTexturesResolution(aChangedSettings.textures.resolutionLimit);
            }

            if (aChangedSettings.textures.hasOwnProperty("frustumCulling")) {
                this.setViewFrustumCullingForTexturesSwitching(aChangedSettings.textures.frustumCulling);
            }

            if (aChangedSettings.textures.hasOwnProperty("enableCompression")) {
                this.setEnableTexturesCompression();
            }

            if (aChangedSettings.textures.hasOwnProperty("disableTextures")) {
                this.sceneAPI.viewModeModule.scheduleRedraw();
            }
        }
    }
}

Scene.registerSceneAPIModule(RenderSettingsModule);
